import React, { Component } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Collapse,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BugReportIcon from '@mui/icons-material/BugReport';

const ErrorContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '400px',
  padding: theme.spacing(4),
  textAlign: 'center',
}));

const ErrorIcon = styled(ErrorOutlineIcon)(({ theme }) => ({
  fontSize: '4rem',
  color: theme.palette.error.main,
  marginBottom: theme.spacing(2),
}));

const ErrorCard = styled(Card)(({ theme }) => ({
  maxWidth: '600px',
  width: '100%',
  boxShadow: theme.shadows[8],
}));

/**
 * Error Boundary component for graceful error handling
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to an error reporting service
    console.error('Error caught by boundary:', error, errorInfo);

    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // Report to error logging service
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  toggleDetails = () => {
    this.setState((prevState) => ({
      showDetails: !prevState.showDetails,
    }));
  };

  render() {
    const { hasError, error, errorInfo, showDetails } = this.state;
    const {
      children,
      fallback,
      title = 'Something went wrong',
      subtitle = "We're sorry, but something unexpected happened.",
      showRetryButton = true,
      showReportButton = true,
      variant = 'default',
    } = this.props;

    if (hasError) {
      // Custom fallback UI
      if (fallback) {
        return fallback;
      }

      // Minimal variant
      if (variant === 'minimal') {
        return (
          <Alert
            severity="error"
            action={
              showRetryButton && (
                <Button color="inherit" size="small" onClick={this.handleRetry}>
                  Retry
                </Button>
              )
            }
          >
            {title}
          </Alert>
        );
      }

      // Inline variant
      if (variant === 'inline') {
        return (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">
              <Typography variant="body2" gutterBottom>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="textSecondary">
                  {subtitle}
                </Typography>
              )}
              {showRetryButton && (
                <Box sx={{ mt: 1 }}>
                  <Button size="small" onClick={this.handleRetry}>
                    Try Again
                  </Button>
                </Box>
              )}
            </Alert>
          </Box>
        );
      }

      // Default full error boundary UI
      return (
        <ErrorContainer>
          <ErrorCard>
            <CardContent>
              <ErrorIcon />

              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                {title}
              </Typography>

              <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                {subtitle}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
                {showRetryButton && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleRetry}
                  >
                    Try Again
                  </Button>
                )}

                {showReportButton && (
                  <Button
                    variant="outlined"
                    startIcon={<BugReportIcon />}
                    onClick={() => {
                      // You can implement error reporting here
                      console.log('Report error:', error);
                    }}
                  >
                    Report Issue
                  </Button>
                )}
              </Box>

              {/* Development mode error details */}
              {process.env.NODE_ENV === 'development' && error && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    onClick={this.toggleDetails}
                    endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mb: 1 }}
                  >
                    {showDetails ? 'Hide' : 'Show'} Error Details
                  </Button>

                  <Collapse in={showDetails}>
                    <Alert severity="warning" sx={{ textAlign: 'left' }}>
                      <Typography
                        variant="caption"
                        component="div"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        <strong>Error:</strong> {error.toString()}
                      </Typography>
                      {errorInfo && (
                        <Typography
                          variant="caption"
                          component="div"
                          sx={{ fontFamily: 'monospace', mt: 1 }}
                        >
                          <strong>Component Stack:</strong>
                          <pre style={{ fontSize: '11px', overflow: 'auto' }}>
                            {errorInfo.componentStack}
                          </pre>
                        </Typography>
                      )}
                    </Alert>
                  </Collapse>
                </Box>
              )}
            </CardContent>
          </ErrorCard>
        </ErrorContainer>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  showRetryButton: PropTypes.bool,
  showReportButton: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'minimal', 'inline']),
  onError: PropTypes.func,
  onRetry: PropTypes.func,
};

export default ErrorBoundary;
